import { io, Socket } from 'socket.io-client';

/**
 * Client-side service for managing order presence via WebSocket
 * 
 * Features:
 * - Connects to order presence WebSocket gateway
 * - Manages tab session ID in sessionStorage
 * - Sends heartbeats to keep session alive
 * - Handles join/leave/presence updates
 * - Uses Socket.IO client for real-time communication
 */

export interface PresenceState {
  ownerUserId: string | null;
  ownerName: string | null;
  showBanner: boolean;
}

export interface PresenceUpdateEvent {
  orderId: number;
  ownerUserId: string | null;
  ownerName: string | null;
}

type PresenceCallback = (state: PresenceState) => void;

class OrderPresenceService {
  private socket: Socket | null = null
  private readonly tabSessionId: string
  private heartbeatInterval: NodeJS.Timeout | null = null
  private currentUserId: string | null = null
  private readonly presenceCallbacks: Map<number, PresenceCallback[]> = new Map()
  private readonly activeOrders: Set<number> = new Set() // Track orders this tab is in
  private readonly HEARTBEAT_INTERVAL = 60000 // 1 minute

  constructor() {
    this.tabSessionId = this.getOrCreateTabSessionId()
  }

  /**
   * Get or create a unique session ID for this browser tab
   */
  private getOrCreateTabSessionId(): string {
    const key = 'ts-order-presence-tab-session'
    let sessionId = sessionStorage.getItem(key)

    if (!sessionId) {
      sessionId = `tab_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`
      sessionStorage.setItem(key, sessionId)
    }

    return sessionId
  }

  /**
   * Initialize the Socket.IO connection
   */
  public connect(baseUrl: string | undefined, userId: string): void {
    if (!baseUrl) {
      return
    }
    if (this.socket) {
      if (this.socket.connected) {
        this.currentUserId = userId
        return
      }
      this.socket.removeAllListeners()
      this.socket.disconnect()
      this.socket = null
    }

    this.currentUserId = userId

    try {
      this.socket = io(`${baseUrl}/order-presence`, {
        transports: ['websocket'],
        reconnection: true,
        reconnectionAttempts: 5,
        reconnectionDelay: 1000,
        auth: (cb) => cb({ token: localStorage.getItem('access_token') }),
      })

      this.socket.on('connect', () => {
        // Don't start heartbeat until user joins an order
      })

      this.socket.on('disconnect', () => {
        this.stopHeartbeat()
        this.activeOrders.clear()
      })

      this.socket.on('connect_error', (error) => {
        console.error('[OrderPresence] Connection error:', error)
      })

      this.socket.on('order:joinResult', (data) => {
        this.handleJoinResult(data)
      })

      this.socket.on('order:presenceUpdated', (data) => {
        this.handlePresenceUpdate(data)
      })

      this.socket.on('order:error', (data) => {
        console.error('[OrderPresence] Server error:', data)
      })
    } catch (error) {
      console.error('[OrderPresence] Failed to connect:', error)
    }
  }

  /**
   * Handle join result from server
   */
  private handleJoinResult(data: any): void {
    const { orderId, showBanner, ownerUserId, ownerName } = data

    const callbacks = this.presenceCallbacks.get(orderId)
    if (callbacks) {
      callbacks.forEach((callback) => {
        callback({
          ownerUserId,
          ownerName,
          showBanner,
        })
      })
    }
  }

  private handlePresenceUpdate(data: PresenceUpdateEvent): void {
    const { orderId, ownerUserId, ownerName } = data
    const showBanner = ownerUserId !== null && ownerUserId !== this.currentUserId

    const callbacks = this.presenceCallbacks.get(orderId)
    if (callbacks) {
      callbacks.forEach((callback) => {
        callback({
          ownerUserId,
          ownerName,
          showBanner,
        })
      })
    }
  }

  /**
   * Start sending heartbeats
   */
  private startHeartbeat(): void {
    if (this.heartbeatInterval) {
      return
    }

    this.heartbeatInterval = setInterval(() => {
      this.heartbeat()
    }, this.HEARTBEAT_INTERVAL)
  }

  /**
   * Stop sending heartbeats
   */
  private stopHeartbeat(): void {
    if (this.heartbeatInterval) {
      clearInterval(this.heartbeatInterval)
      this.heartbeatInterval = null
    }
  }

  /**
   * Send heartbeat to server
   */
  private heartbeat(): void {
    if (!this.socket || !this.socket.connected) {
      return
    }

    this.socket.emit('order:heartbeat', {
      tabSessionId: this.tabSessionId,
    })
  }

  /**
   * Disconnect from WebSocket
   */
  public disconnect(): void {
    this.stopHeartbeat()
    this.activeOrders.clear()

    if (this.socket) {
      this.socket.removeAllListeners()
      this.socket.disconnect()
      this.socket = null
    }

    this.presenceCallbacks.clear()
  }

  /**
   * Join an order (notify server of presence)
   * User identity is extracted from JWT token on the server
   */
  public joinOrder(orderId: number): void {
    if (!this.socket || !this.socket.connected) {
      return
    }

    // Track this order as active
    const wasEmpty = this.activeOrders.size === 0
    this.activeOrders.add(orderId)

    // Start heartbeat when joining first order
    if (wasEmpty) {
      this.startHeartbeat()
    }

    this.socket.emit('order:join', {
      orderId: Number(orderId),
      tabSessionId: String(this.tabSessionId),
    })
  }

  public leaveOrder(orderId: number): void {
    if (!this.socket || !this.socket.connected) {
      return
    }

    // Remove this order from active set
    this.activeOrders.delete(orderId)

    // Stop heartbeat when leaving last order
    if (this.activeOrders.size === 0) {
      this.stopHeartbeat()
    }

    this.socket.emit('order:leave', {
      orderId: Number(orderId),
      tabSessionId: String(this.tabSessionId),
    })
  }

  /**
   * Get current presence for an order
   */
  public getPresence(orderId: number): void {
    if (!this.socket || !this.socket.connected) {
      return
    }

    this.socket.emit('order:getPresence', { orderId })
  }

  /**
   * Subscribe to presence updates for an order
   */
  public onPresenceChange(orderId: number, callback: PresenceCallback): () => void {
    if (!this.presenceCallbacks.has(orderId)) {
      this.presenceCallbacks.set(orderId, [])
    }

    this.presenceCallbacks.get(orderId)!.push(callback)

    return () => {
      const callbacks = this.presenceCallbacks.get(orderId)
      if (callbacks) {
        const index = callbacks.indexOf(callback)
        if (index > -1) {
          callbacks.splice(index, 1)
        }

        if (callbacks.length === 0) {
          this.presenceCallbacks.delete(orderId)
        }
      }
    }
  }

  /**
   * Check if connected
   */
  public isConnected(): boolean {
    return this.socket !== null && this.socket.connected
  }
}

export const orderPresenceService = new OrderPresenceService();
